import React, { useEffect, useRef, useState } from "react";
import { X, Church, ShieldCheck, Volume2, VolumeX, Pencil, ArrowRight, Send, Check } from "lucide-react";
import { resolveMediaUrl } from "@/components/FileUpload";
import { NEUTRAL_POSTER } from "@/lib/neutralPoster";
import { gradientCss } from "@/lib/gradients";
import { LinkPreviewCard } from "@/components/LinkPreviewCard";
import { StoryStickers, StoryDrawing, StoryBlurBackdrop, storyBlurSrc, mediaTransformStyle, visibleStickers, hasLinkSticker } from "@/components/StoryMedia";
import { bgStyleOf } from "./storyEditorUtils";
import { imageVariant } from "@/lib/mediaUrl";

// Lot 737 — mêmes réactions que les publications (👍 ❤️ 🙏 😢 😂 😮, sans 🙌) : « les gens recherchent pleurer, rire, ok… ».
const QUICK_REACTIONS = ["👍", "❤️", "🙏", "😢", "😂", "😮"];

// Aperçu plein écran : rendu identique à la visionneuse des abonnés (barre de progression, auteur,
// musique, mentions, barre de réponse) + « Modifier » / « Partager » (ou « Enregistrer » en édition).
// Le son est réel : musique choisie (depuis son point de départ, au volume réglé) et son de la vidéo, coupables d'un tap.
export const StoryPreview = ({ author, stage, media, mediaSrc, transform, bg, stickers, drawingUrl, linkEmbed, music, musicVolume = 1, mentions, editMode, busy, onEdit, onShare, t }) => {
  const [muted, setMuted] = useState(false);
  const musicRef = useRef(null);
  useEffect(() => {
    const a = musicRef.current;
    if (!a || !music?.url) return undefined;
    a.src = resolveMediaUrl(music.url);
    a.currentTime = music.start || 0;
    a.loop = true;
    a.volume = Math.max(0, Math.min(1, musicVolume));
    a.muted = muted;
    a.play().catch(() => {});
    return () => { try { a.pause(); } catch { /* noop */ } };
  }, [music?.url, music?.start, musicVolume, muted]);
  // Vidéo : lecture avec son ; si le navigateur refuse l'autoplay sonore, on repart en muet plutôt que figé.
  const videoRef = useRef(null);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = muted || !!music;
    v.play().catch(() => { v.muted = true; setMuted(true); v.play().catch(() => {}); });
  }, [mediaSrc, muted, music]);
  const hasSound = media?.media_type === "video" || !!music;
  const embed = linkEmbed?.url ? linkEmbed : null;
  const linkSticker = !!embed && hasLinkSticker(stickers);
  const linkOnly = !!embed && !linkSticker && !visibleStickers(stickers, embed).some((s) => s.type === "text");
  return (
  <div className="absolute inset-0 z-[70] bg-black select-none" data-testid="story-fs-preview">
    {music?.url && <audio ref={musicRef} className="hidden" data-testid="story-fs-preview-audio" />}
    <div className="absolute inset-0 overflow-hidden" style={{ containerType: "inline-size" }}>
      {stage === "text" ? (
        <div className="absolute inset-0 flex items-center justify-center px-8" style={bgStyleOf(bg, gradientCss)} data-testid="story-fs-preview-text" data-link-only={linkOnly ? "1" : undefined}>
          {bg.startsWith("/verse-bg/") && <div className="absolute inset-0 bg-black/30" aria-hidden />}
          {embed && !linkSticker && <div className={`relative w-full z-10 ${linkOnly ? "max-w-sm" : "max-w-xs"}`}><LinkPreviewCard embed={embed} large={linkOnly} testId="story-fs-preview-link" /></div>}
        </div>
      ) : media && (
        <>
          <StoryBlurBackdrop src={storyBlurSrc(media, mediaSrc)} testId="story-fs-preview-blur" />
          {media.media_type === "video"
            ? <video ref={videoRef} src={mediaSrc} poster={NEUTRAL_POSTER} autoPlay muted={muted || !!music} loop playsInline className="absolute inset-0 w-full h-full object-contain" style={mediaTransformStyle(transform)} data-testid="story-fs-preview-video" />
            : <img src={mediaSrc} alt="" className="absolute inset-0 w-full h-full object-contain" style={mediaTransformStyle(transform)} data-testid="story-fs-preview-image" />}
        </>
      )}
      <StoryDrawing url={drawingUrl} testId="story-fs-preview-drawing" />
      <StoryStickers stickers={stickers} embed={embed} testId="story-fs-preview-stickers" />
    </div>

    {/* Barre de progression (1 segment, en boucle) */}
    <div className="absolute left-2 right-2 z-10" style={{ top: "calc(var(--sa-top, 0px) + 8px)" }} data-testid="story-fs-preview-progress">
      <div className="h-1 rounded-full bg-white/30 overflow-hidden"><div className="h-full bg-white" style={{ animation: "storyProgress 5000ms linear infinite" }} /></div>
    </div>

    {/* En-tête auteur — comme dans la visionneuse */}
    <div className="absolute left-3 right-3 flex items-center gap-2 text-white z-10" style={{ top: "calc(var(--sa-top, 0px) + 20px)" }} data-testid="story-fs-preview-header">
      {author?.is_church ? (
        <div className="h-9 w-9 rounded-full grid place-items-center border-2 border-white shrink-0" style={{ background: "var(--yalola-primary)" }}><Church size={17} /></div>
      ) : author?.avatar_url ? (
        <img src={imageVariant(author.avatar_url, 48)} alt="" className="h-9 w-9 rounded-full object-cover border-2 border-white shrink-0" />
      ) : (
        <div className="h-9 w-9 rounded-full grid place-items-center border-2 border-white shrink-0 text-sm font-bold" style={{ background: "var(--yalola-primary)" }}>{(author?.name || "?").trim()[0]?.toUpperCase()}</div>
      )}
      <div className="min-w-0">
        <div className="text-sm font-semibold flex items-center gap-1 truncate" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.5)" }}>
          <span className="truncate" data-testid="story-fs-preview-author">{author?.name}</span>
          {author?.is_church && <ShieldCheck size={14} className="shrink-0" />}
        </div>
        <div className="text-[10px] opacity-90 leading-none" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.5)" }}>{author?.is_church ? t("story.official_church", "Église officielle") : t("story.preview_just_now", "À l'instant")}</div>
      </div>
      <div className="ml-auto flex items-center gap-2 shrink-0">
        {hasSound && (
          <button type="button" onClick={() => setMuted((m) => !m)} className="text-white bg-black/40 rounded-full h-9 w-9 grid place-items-center" aria-label={muted ? t("live.unmute", "Activer le son") : t("live.mute", "Couper le son")} data-testid="story-fs-preview-mute">
            {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
        )}
        <button type="button" onClick={onEdit} className="text-white bg-black/40 rounded-full h-9 w-9 grid place-items-center" aria-label={t("common.close", "Fermer")} data-testid="story-fs-preview-close"><X size={18} /></button>
      </div>
    </div>

    {(music || (mentions || []).length > 0) && (
      <div className="absolute left-3 right-3 z-10 flex flex-col gap-1.5 items-start" style={{ top: "calc(var(--sa-top, 0px) + 64px)" }}>
        {music && <span className="inline-flex items-center gap-1.5 rounded-full bg-black/50 text-white px-2.5 py-1 text-xs font-semibold" data-testid="story-fs-preview-music">♪ {music.title || t("story.music", "Musique")}</span>}
        {(mentions || []).length > 0 && (
          <div className="flex flex-wrap gap-1" data-testid="story-fs-preview-mentions">
            {mentions.map((m) => <span key={m.id} className="rounded-full bg-white/20 backdrop-blur-sm text-white px-2 py-0.5 text-xs font-semibold">@{m.name}</span>)}
          </div>
        )}
      </div>
    )}

    {/* Barre de réponse telle que la verront les abonnés (inerte) + actions */}
    <div className="absolute inset-x-0 bottom-0 z-10 px-3 pt-12 space-y-3" style={{ background: "linear-gradient(0deg, rgba(0,0,0,0.75), transparent)", paddingBottom: "calc(var(--sa-bottom, 0px) + 12px)" }}>
      <div className="flex items-center justify-center gap-2" data-testid="story-fs-preview-actions">
        <button type="button" onClick={onEdit} className="h-11 px-4 rounded-full bg-white/15 backdrop-blur-xl border border-white/25 text-white text-sm font-bold flex items-center gap-1.5 active:scale-95 transition-transform" data-testid="story-fs-preview-edit">
          <Pencil size={15} /> {t("story.preview_edit", "Modifier")}
        </button>
        <button type="button" onClick={onShare} disabled={busy} className="h-11 px-5 rounded-full text-white text-sm font-bold flex items-center gap-1.5 active:scale-95 transition-transform disabled:opacity-50" style={{ background: "var(--yalola-primary)", boxShadow: "0 8px 24px -6px var(--yalola-primary)" }} data-testid="story-fs-preview-share">
          {editMode ? <><Check size={16} /> {t("story.save", "Enregistrer")}</> : <>{t("story.publish_now", "Partager")} <ArrowRight size={16} /></>}
        </button>
      </div>
      <p className="text-center text-[11px] text-white/70" data-testid="story-fs-preview-hint">{t("story.preview_hint", "Voici ce que verront tes abonnés")}</p>
      <div className="pointer-events-none opacity-80" aria-hidden>
        <div className="flex items-center justify-center gap-3">
          {QUICK_REACTIONS.map((e) => <span key={e} className="text-2xl">{e}</span>)}
        </div>
        <div className="flex items-center gap-2 mt-2">
          <div className="flex-1 rounded-full bg-white/15 border border-white/40 text-white/70 px-4 py-2.5 text-sm">{t("story.reply_placeholder", "Répondre...")}</div>
          <span className="h-10 w-10 rounded-full grid place-items-center text-white shrink-0" style={{ background: "var(--yalola-primary)" }}><Send size={16} /></span>
        </div>
      </div>
    </div>
  </div>
  );
};
